import {
  isNaN,
  transform,
  isFunction,
  has,
  get,
  isUndefined,
  isArray,
  isPlainObject,
  cloneDeep,
  isEmpty,
} from 'lodash';

export function getFileNameFromResponseHeaders(headers) {
  const disposition = headers.get('Content-Disposition');
  if (isEmpty(disposition)) {
    return '';
  }
  let filename = disposition.split(/;(.+)/)[1].split(/=(.+)/)[1];
  if (filename.toLowerCase().startsWith("utf-8''")) {
    filename = decodeURIComponent(filename.replace("utf-8''", ''));
  } else {
    filename = filename.replace(/['"]/g, '');
  }
  return filename;
}

export const toNumber = (value) => {
  const number = parseInt(value, 10);
  if (isNaN(number)) return undefined;
  return number;
};

export const toFloat = (value) => {
  const number = parseFloat(value);
  if (isNaN(number)) return undefined;
  return number;
};

export const getEventPrefixForLink = () => 'link_';

/**
 * Creates a deep clone of an object but with some key/values replaced/removed
 *
 * @param {object} source the source object to be transformed
 * @param {object} transformationsObject an object specifying key/value pairs. All keys in source that match a key in
 * transformationsObject will be replaced by the value at that key in transformationsObject.
 * (if value is a function it will be invoked (with the source value as param) and the result assigned as the value)
 * (if value is undefined - but key exists - that key is not added to the target)
 * @param {object} target the object/array in which to save the transformed results
 *
 * ie.
 * transformPropsDeep({
 *         a:1,
 *         b:{a:2},
 *         c:'a',
 *         e:'string'
 *     },
 *     {
 *         a:0, // all keys named "a" will get a value of 0 in the transformed object
 *         c: ()=>5, // all keys named "c" will get 5 in the transformed object
 *         e:undefined // all keys named "e" will be removed from the transformed object
 *     })
 * will return a (deeply) new object as {a:0,b:{a:0},c:5}
 */
export const transformPropsDeep = (
  source,
  transformationsObject = {},
  target = {},
  currentPath,
) =>
  transform(
    source,
    (accumulator, value, key, context) => {
      let path;
      if (!currentPath) {
        path = key;
      } else {
        path = `${currentPath}.${key}`;
      }
      if (has(transformationsObject, key)) {
        let transformedValue = null;
        const replacement = get(transformationsObject, key);
        if (isFunction(replacement)) {
          transformedValue = replacement(value, context, path);
        } else {
          transformedValue = replacement;
        }

        if (!isUndefined(transformedValue)) {
          accumulator[key] = transformedValue;
        }
      } else if (isArray(value)) {
        accumulator[key] = transformPropsDeep(
          value,
          transformationsObject,
          [],
          path,
        );
      } else if (isPlainObject(value)) {
        accumulator[key] = transformPropsDeep(
          value,
          transformationsObject,
          {},
          path,
        );
      } else {
        accumulator[key] = value;
      }
    },
    target,
  );

export function escapeHTML(html = '') {
  const regexEscape = /["&'<>`]/g;
  const escapeMap = {
    '"': '&quot;',
    '&': '&amp;',
    "'": '&#x27;',
    '<': '&lt;',
    '>': '&gt;',
    '`': '&#x60;',
  };
  if (typeof html === 'string') {
    return html.replace(regexEscape, (match) => escapeMap[match]);
  }
  return html;
}
/**
 * Creates a new object out of a target object and a list of update objects
 * by updating the target leaves only.
 * So it will not add properties and it will not update/modify a target property if
 * it is not a leaf or if new value is undefined
 *
 * @param {object} target the object to be transformed
 * @param(s) {object(s)} updates the objects to merge into the target object
 *
 */
export function updateObjectLeaves(
  { data: target, restrictions = {} },
  ...updates
) {
  let mergedObject = target;
  updates.forEach((updateObject) => {
    mergedObject = transform(
      mergedObject,
      (accumulator, value, key) => {
        if (isPlainObject(value) && has(updateObject, key)) {
          accumulator[key] = updateObjectLeaves(
            { data: value, restrictions: restrictions[key] },
            updateObject[key],
          );
        } else if (has(updateObject, key) && !isUndefined(updateObject[key])) {
          let canUpdate = true;
          const restriction = restrictions[key];
          if (isFunction(restriction)) {
            canUpdate = restriction(updateObject[key]);
          }
          if (canUpdate) {
            accumulator[key] = cloneDeep(updateObject[key]);
          } else {
            accumulator[key] = cloneDeep(value);
          }
        } else {
          accumulator[key] = cloneDeep(value);
        }
        return accumulator;
      },
      {},
    );
  });

  return mergedObject;
}
